import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Certificate, UserGear, SignOut, Sun, Moon, ArrowsClockwise } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { ThemeToggle } from "@/lib/theme";
import { visibleModules } from "@/lib/perms";
import { fdate } from "@/lib/format";
import { fileUrl } from "@/components/ImageUpload";
import RenewDialog from "@/components/RenewDialog";
import ConnectionBanner from "@/components/ConnectionBanner";
import DailyAgendaReminder from "@/components/DailyAgendaReminder";
import ErrorBoundary from "@/components/ErrorBoundary";
import { Button } from "@/components/ui/button";

const BRAND_LOGO = "/logo-poss.png";

export default function Layout() {
  const { user, logout, refresh } = useAuth();
  const { dark, setDark } = ThemeToggle();
  const navigate = useNavigate();
  const [renewOpen, setRenewOpen] = useState(false);

  const isSuper = user?.role === "admin";
  const isEmpresaAdmin = user?.role === "empresa_admin";
  const company = user?.company;
  const lic = user?.license;

  let nav = [];
  if (isSuper) {
    nav = [{ to: "/licencias", label: "Licencias SaaS", icon: Certificate }];
  } else {
    nav = [...visibleModules(user)];
    if (isEmpresaAdmin) nav.push({ to: "/usuarios", label: "Usuarios y permisos", icon: UserGear });
  }

  const logoSrc = company?.logo ? fileUrl(company.logo) : BRAND_LOGO;
  const title = isSuper ? "Inmobiliaria POSS" : company?.empresa || "Inmobiliaria POSS";
  const subtitle = isSuper ? "Panel Superadmin" : company?.codigo || "";
  const diasTone = lic?.dias_restantes != null && lic.dias_restantes <= 30 ? "text-amber-400" : "text-neutral-400";

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      <aside className="w-64 shrink-0 brand-dark flex flex-col fixed h-screen border-r border-white/10">
        <div className="h-20 flex items-center gap-3 px-5 border-b border-white/10">
          <img src={logoSrc} alt={title} className="w-11 h-11 rounded-lg object-cover ring-1 ring-amber-400/30" />
          <div className="leading-tight min-w-0">
            <div className="font-display font-extrabold text-sm truncate">
              {isSuper ? (<>Inmobiliaria <span className="brand-gold-text">POSS</span></>) : title}
            </div>
            <div className="text-[10px] text-amber-400/70 uppercase tracking-[0.15em] truncate">{subtitle || "Administración"}</div>
          </div>
        </div>

        {!isSuper && lic && (
          <div className="px-5 py-3 border-b border-white/10" data-testid="license-box">
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-wide text-amber-400/70">Licencia</span>
              <span className="text-[10px] font-mono text-neutral-400" data-testid="license-version">v{lic.version || "1.0.0"}</span>
            </div>
            <div className="text-xs text-neutral-300 mt-0.5">Vence: {fdate(lic.fecha_vencimiento)}</div>
            <div className={`text-xs font-medium ${diasTone}`} data-testid="license-days">
              {lic.dias_restantes != null ? `${lic.dias_restantes} días restantes` : "Sin vencimiento"}
            </div>
            {isEmpresaAdmin && (
              <Button size="sm" data-testid="sidebar-renew-btn"
                className="w-full mt-2 rounded-lg gap-1.5 bg-gradient-to-r from-amber-400 to-yellow-600 text-black hover:opacity-90"
                onClick={() => setRenewOpen(true)}>
                <ArrowsClockwise size={14} /> Renovar licencia
              </Button>
            )}
          </div>
        )}

        <nav className="flex-1 py-3 px-3 space-y-1 overflow-y-auto">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`nav-${n.to.slice(1)}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  isActive
                    ? "bg-gradient-to-r from-amber-400 to-yellow-600 text-black shadow-lg shadow-amber-500/20"
                    : "text-neutral-400 hover:bg-white/5 hover:text-amber-200"
                }`}>
              <n.icon size={18} weight="duotone" />
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-white/10">
          <div className="text-xs font-semibold text-neutral-200 truncate">{user?.name}</div>
          <div className="text-[10px] text-amber-400/60 uppercase tracking-wide mb-2">
            {isSuper ? "Superadmin" : isEmpresaAdmin ? "Administrador" : "Usuario"}
          </div>
          <Button data-testid="logout-btn" variant="outline" size="sm"
            className="w-full justify-start gap-2 rounded-lg border-white/15 bg-white/5 text-neutral-200 hover:bg-white/10 hover:text-white"
            onClick={async () => { await logout(); navigate("/login"); }}>
            <SignOut size={16} /> Cerrar sesión
          </Button>
        </div>
      </aside>

      <div className="flex-1 ml-64 flex flex-col">
        <div className="sticky top-0 z-20">
          <ConnectionBanner />
          <header className="h-16 border-b border-border bg-card/80 backdrop-blur flex items-center justify-end px-6 gap-3">
          <Button data-testid="theme-toggle" variant="ghost" size="icon" className="rounded-lg" onClick={() => setDark(!dark)}>
            {dark ? <Sun size={18} /> : <Moon size={18} />}
          </Button>
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-400 to-yellow-600 text-black flex items-center justify-center font-display font-bold text-sm">
            {(user?.name || "?").charAt(0).toUpperCase()}
          </div>
          </header>
        </div>
        <main className="flex-1 p-6 max-w-[1600px] w-full">
          <ErrorBoundary><Outlet /></ErrorBoundary>
        </main>
      </div>
      <DailyAgendaReminder />
      <RenewDialog open={renewOpen} onOpenChange={setRenewOpen} empresa={company?.empresa} precio={lic?.precio} onDone={refresh} />
    </div>
  );
}
